import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const openMenus = new WeakMap();

export default function PlayerMenu({
  player,
  anchorRef,
  label,
  onClose,
  children,
  width = 360
}) {
  const menu = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [position, setPosition] = useState({ visibility: "hidden" });
  const root = player && player.el();

  useLayoutEffect(() => {
    if (!root) return;
    const place = () => {
      if (!anchorRef.current || !menu.current) return;
      const bounds = root.getBoundingClientRect();
      const anchor = anchorRef.current.getBoundingClientRect();
      const bar = anchorRef.current.closest(".vjs-control-bar");
      const controlsTop = Math.min(
        anchor.top,
        bar ? bar.getBoundingClientRect().top : anchor.top
      );
      const panelWidth = Math.min(width, Math.max(0, bounds.width - 24));
      setPosition({
        width: panelWidth,
        right: Math.max(
          12,
          Math.min(bounds.width - panelWidth - 12, bounds.right - anchor.right)
        ),
        bottom: Math.max(12, bounds.bottom - controlsTop + 12),
        maxHeight: Math.max(80, controlsTop - Math.max(bounds.top, 0) - 24),
        visibility: "visible"
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    player.on("fullscreenchange", place);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    if (observer) observer.observe(root);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      player.off("fullscreenchange", place);
      if (observer) observer.disconnect();
    };
  }, [player, root, anchorRef, width]);

  useEffect(() => {
    if (!root) return;
    const onOutside = (event) => {
      if (
        menu.current &&
        !menu.current.contains(event.target) &&
        (!anchorRef.current || !anchorRef.current.contains(event.target))
      )
        close.current(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close.current(true);
      } else if (
        [
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Home",
          "End"
        ].includes(event.key) &&
        menu.current &&
        menu.current.contains(event.target)
      ) {
        const group = event.target.closest("[role='radiogroup']");
        if (!group) return;
        const choices = Array.from(group.querySelectorAll("[role='radio']"));
        const current = choices.indexOf(event.target);
        if (current < 0) return;
        event.preventDefault();
        event.stopPropagation();
        const direction =
          event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1 : 1;
        const next =
          event.key === "Home"
            ? 0
            : event.key === "End"
            ? choices.length - 1
            : (current + direction + choices.length) % choices.length;
        choices[next].focus();
        choices[next].click();
      }
    };
    const onAnotherMenu = () => close.current(false);
    root.dispatchEvent(new Event("atlas-player-menu-open"));
    root.addEventListener("atlas-player-menu-open", onAnotherMenu);
    document.addEventListener("pointerdown", onOutside);
    document.addEventListener("keydown", onKey, true);
    openMenus.set(root, (openMenus.get(root) || 0) + 1);
    root.classList.add("atlas-menu-open");
    player.userActive(true);
    const first = menu.current.querySelector("[aria-checked='true'], button");
    if (first) first.focus();
    return () => {
      root.removeEventListener("atlas-player-menu-open", onAnotherMenu);
      document.removeEventListener("pointerdown", onOutside);
      document.removeEventListener("keydown", onKey, true);
      const remaining = (openMenus.get(root) || 1) - 1;
      openMenus.set(root, remaining);
      if (!remaining) root.classList.remove("atlas-menu-open");
    };
  }, [player, root, anchorRef]);

  return root
    ? createPortal(
        <div
          ref={menu}
          className="atlas-player-menu"
          role="dialog"
          aria-label={label}
          style={position}
        >
          {children}
        </div>,
        root
      )
    : null;
}
